import { ref } from "vue";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useAuthStore } from "./stores/auth";
import DeepLinkRequest from "./requests/DeepLinkRequest";
import { useTenantConfig } from "./composables/useTenantConfig";
import tenantService from "./services/tenant-service";
import { Tenants } from "./constants/Tenants";
import loggingService from "./services/logging-service";
import memberLogRequest from "./requests/MemberLogRequest";
import { usePageMeta } from "./composables/usePageMeta";
import { pushDataLayer } from "./utils/gtm";

const routes: Array<RouteRecordRaw> = [
    {
        path: "/",
        name: "root",
        meta: {
            title:
                "Sign In/ Join Now for your loyalty to be rewarded with {siteName}",
            description:
                "Sign in/Join Now to take your loyalty to the next level with {siteName}, the official rewards programme of {siteName}",
        },
        component: () => import("./views/IndexPageView.vue"),
    },
    {
        path: "/home",
        name: "home",
        meta: {
            title: "Home - Your Account - {siteName}",
            description:
                "Click here to check your recent activity, collect and spend your points at {siteName}",
            requireAuth: true,
        },
        component: () => import("./views/HomePageView.vue"),
    },
    {
        path: "/collect/bet365",
        name: "collect-betting-bet365",
        component: () => import("./views/Bet365View.vue"),
        meta: {
            title: "Collect points from bet365 with {siteName}",
            description:
                "Enjoy collecting points when you shop online with bet365. Just remember to click through from {siteName} so we can track and reward your purchase. ",
            requireAuth: true,
        },
    },
    {
        path: "/collect/betfred",
        name: "collect-betting-betfred",
        component: () => import("./views/BetfredView.vue"),
        meta: {
            title: "Collect points from betfred with {siteName}",
            description:
                "Enjoy collecting points when you shop online with betfred. Just remember to click through from {siteName} so we can track and reward your purchase. ",
            requireAuth: true,
        },
    },
    {
        path: "/collect/paddy-power",
        name: "collect-betting-paddy-power",
        component: () => import("./views/PaddyPower.vue"),
        meta: {
            title: "Collect points from paddy-power with {siteName}",
            description:
                "Enjoy collecting points when you shop online with paddy-power. Just remember to click through from {siteName} so we can track and reward your purchase. ",
            requireAuth: true,
        },
    },
    {
        path: "/collect/:RetailerPath",
        name: "collect",
        component: () => import("./views/CollectRetailerView.vue"),
        meta: {
            title: "Collect points with retailers - {siteName}",
            description:
                "Enjoy collecting points when you shop online with your favourite retailer. Just remember to click through from {siteName} so we can track and reward your purchase.",
            requireAuth: true,
        },
    },
    {
        path: "/signin",
        name: "signin",
        meta: {
            title: "Sign In/Join Now for your loyalty to be rewarded with {siteName}",
            description:
                "Sign in/Join Now to take your loyalty to the next level with {siteName}, the official rewards programme of {siteName}.",
        },
        component: () => import("./views/JoinNowSignInView.vue"),
    },
    {
        path: "/join",
        name: "join",
        meta: {
            title: "Sign In/Join Now for your loyalty to be rewarded with {siteName}",
            description:
                "Sign in/Join Now to take your loyalty to the next level with {siteName}, the official rewards programme of {siteName}.",
        },
        component: () => import("./views/JoinNowSignInView.vue"),
    },
    {
        path: "/retailer-redirect",
        name: "retailer-redirect",
        component: () => import("./views/RetailerRedirect.vue"),
        meta: {
            requireAuth: true,
        },
    },
    {
        path: "/wsp-deep-link",
        name: "wsp-deep-link",
        component: () => import("./views/WspDeepLinkView.vue"),
        meta: {
            requireAuth: true,
        },
    },
    {
        path: "/my-account",
        name: "my-account",
        component: () => import("./views/MyAccountView.vue"),
        meta: {
            title: "My Account",
            requireAuth: true,
        },
        children: [
            {
                path: "",
                redirect: { name: "account-statement" },
            },
            {
                path: "statement",
                name: "account-statement",
                component: () => import("./components/MyStatement.vue"),
                meta: {
                    title: "Your statement with {siteName}",
                    description: "Your statement with {siteName}",
                },
                alias: "/statement",
            },
            {
                path: "my-profile",
                name: "account-profile",
                component: () => import("./components/MyProfile.vue"),
                meta: {
                    title: "Your account with {siteName}",
                    description: "Your account with {siteName}",
                },
                alias: "/my-profile",
            },
            {
                path: "contact-preferences",
                name: "account-contact-preferences",
                component: () => import("./components/ContactPreferences.vue"),
                meta: {
                    title: "Contact Preferences",
                    description: "Contact Preferences",
                },
                alias: "/contact-preferences",
            },
        ],
    },
    {
        path: "/forgot-password",
        name: "forgot-password",
        meta: {
            title: "Forgotten Password",
        },
        component: () => import("./views/ForgotPasswordView.vue"),
    },
    {
        path: "/reset-password",
        name: "reset-password",
        meta: {
            title: "Reset your th {siteName} password",
        },
        component: () => import("./views/ResetPasswordView.vue"),
    },
    {
        path: "/my-account/change-password",
        name: "change-password",
        component: () => import("./views/ChangePasswordView.vue"),
        meta: {
            title: "Change Password",
            requireAuth: true,
        },
    },
    {
        path: "/spend",
        name: "sprend",
        component: () => import("./views/SpendView.vue"),
        meta: {
            requireAuth: true,
            title: "Spend Your Points - {siteName}",
            description:
                "Use your {siteName} points to purchase on tickets, season tickets and memberships at {siteName}",
        },
    },
    {
        path: "/spend/my-goal",
        name: "my-goal",
        component: () => import("./views/MyGoalView.vue"),
        meta: {
            requireAuth: true,
            title: "Your goal with - {siteName}",
            description: "Click here to view your goal with {siteName}",
        },
    },
    {
        path: "/help-centre",
        name: "help-centre",
        meta: {
            title: "Help Centre - {siteName}",
        },
        component: () => import("./views/HelpCentreView.vue"),
    },
    {
        path: "/collect-home/:category?",
        name: "collect-home",
        component: () => import("./views/CollectHomeView.vue"),
        meta: {
            requireAuth: true,
            title: "Collect points with retailers - {siteName}",
        },
    },
    {
        path: "/weekly-trivia",
        name: "weekly-trivia",
        component: () => import("./views/WeeklyTriviaView.vue"),
        meta: {
            title: "Weekly Trivia - {siteName}",
            requireAuth: true,
        },
    },
    {
        path: "/rewards-pass",
        name: "rewards-pass",
        component: () => import("./views/RewardsPassView.vue"),
        meta: {
            requireAuth: true,
            title: "Rewards Pass - {siteName}",
        },
    },
    {
        path: "/activate-rewards-pass",
        name: "activate-rewards-pass",
        component: () => import("./views/ActivateRewardsPassView.vue"),
        meta: {
            requireAuth: true,
            title: "Activate Rewards Pass - {siteName}",
        },
    },
    {
        path: "/spend/save/:VoucherPath",
        name: "spend-on",
        component: () => import("./views/SpendOnView.vue"),
        meta: {
            requireAuth: true,
            title: "Spend Voucher - {siteName}",
            description: "Spend Your Voucher - {siteName}"

        },
    },
    {
        path: "/spend/win",
        name: "win",
        component: () => import("./views/WinView.vue"),
        meta: {
            requireAuth: true,
            title: "Win with {siteName}",
            description: "View our active competitions and be in with a chance to win with {siteName}",
        },
    },
    {
        path: "/spend/win/:CompetitionPath",
        name: "win-competition",
        component: () => import("./views/WinCompetitionView.vue"),
        meta: {
            requireAuth: true,
            title: "Win with {siteName}",
            description: "Enter our competitions and be in with a chance to win with {siteName}",
        },
    },
    {
        path: "/page/howtoshoponline",
        name: "howtoshoponline",
        component: () => import("./views/HowItWorksView.vue"),
        meta: {
            title: "How to shop online with {siteName}",

        },
    },
    {
        path: "/terms/:termsSection?",
        name: "terms",
        meta: {
            title: "Member Agreement - {siteName}",
        },
        component: () => import("./views/MemberAgreementView.vue"),
    },
    {
        path: "/safer-gambling",
        name: "safer-gambling",
        component: () => import("./views/SaferGamblingView.vue"),
    },
    {
        path: "/privacycentre",
        name: "privacy-centre",
        meta: {
            title: "Privacy Centre - Our Privacy Promise",
            description:
                "View or download the details of {siteName} powered by Rewards4 Privacy Policy here. We protect your information, our business depends on it.",
        },
        component: () => import("./views/PrivacyCentreView.vue"),
    },
    {
        path: "/search",
        name: "search-results",
        component: () => import("./views/SearchResultsView.vue"),
        meta: {
            requireAuth: true,
        },
    },
    {
        path: "/missing-retailer/:retailer",
        name: "missing-retailerms",
        component: () => import("./views/MissingRetailerView.vue"),
        meta: {
            requireAuth: true,
        },
    },
    {
        path: "/activate",
        name: "activate",
        meta: {
            title: "Activate Your Account!",
            description: "Activate Your Account!",
        },
        component: () => import("./views/ActivateView.vue"),
    },
    {
        path: "/privacy/:privacySection?",
        name: "privacy",
        component: () => import("./views/PrivacyView.vue"),
    },
    {
        path: "/confirm-password",
        name: "confirm-password",
        meta: {
            title: "Confirm Password",
            description: "Confirm Password",
        },
        component: () => import("./views/ConfirmPasswordView.vue"),
    },
    {
        path: "/promotion/:promotioncode",
        name: "promotion",
        meta: {
            title: "Win fantastic points and prizes with {siteName}!",
        },
        component: () => import("./views/PromotionView.vue"),
    },
    {
        path: "/page/:hubpath",
        name: "hub",
        meta: {
            title: "",
        },
        component: () => import("./views/hubView/HubView.vue"),
    },
    {
        path: "/bettinghub",
        name: "bettinghub",
        meta: {
            title: "",
        },
        component: () => import("./views/hubView/BettingView.vue"),
    },
    {
        path: "/spend/vouchers",
        name: "VoucherListView",
        meta: {
            title: "Spend your points on vouchers",
            requireAuth: true,
        },
        component: () => import("./views/VoucherListView.vue"),
    },
    {
        path: "/collect/partner/:PartnerPath",
        name: "partner",
        meta: {
            requireAuth: true,
        },
        component: () => import("./views/CustomPartnerView.vue"),
    },
    {
        path: "/errors/Error404",
        name: "error404",
        component: () => import("./views/Error404View.vue"),
    },
    {
        path: "/errors/Error500",
        name: "error500",
        component: () => import("./views/Error500View.vue"),
    },
    {
    path: "/partner/:PartnerPath",
    name: "partner-short",
    meta: {
      requireAuth: true,
    },
     component: () => import("./views/CustomPartnerView.vue"),
  	},
    {
        path: "/help-centre/gambling-harm",
        name: "gambling-harm",
        component: () => import("./views/GamblingHarmView.vue"),
    },
    {
        path: "/help-centre/stay-in-control",
        name: "stay-in-control",
        component: () => import("./views/StayInControlView.vue"),
    },
    {
        path: "/help-centre/advice",
        name: "advice",
        component: () => import("./views/GamblingAdviceView.vue"),
    },
    {
        path: "/:pathMatch(.*)*",
        name: "not-found",
        redirect: { name: "error404" },
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        if (to.hash) {
            return { el: to.hash };
        }
        return { top: 0 };
    },
});

router.beforeEach(async (to) => {
    const authStore = useAuthStore();
    const tenantConfig = useTenantConfig();
    const email = to.query.email as string | undefined;
    const emailVerificationCode = to.query.emailverificationcode as
        | string
        | undefined;

    //
    // ✅ Retailer route parameter validation
    //
    const retailerPattern = /^[a-z0-9-]{1,80}$/i;

    if (to.name === "missing-retailerms") {
        const retailer = to.params.retailer as string;

        if (!retailerPattern.test(retailer)) {
            return { path: "/errors/Error404" };
        }
    }

    //if (to.name === "promotion") {
    //    await authStore.signout();
    //}

    if (
        to.name === "howtoshoponline" &&
        tenantService.getTenantName() === Tenants.Rewards4RacingIreland
    ) {
        return { path: "/errors/Error404" };
    }

    const isDeepLink =
        to.name != "activate" &&
        !authStore.isAuthenticated &&
        email &&
        emailVerificationCode;
    if (isDeepLink) {
        const settings = tenantConfig.settings.value?.tenantSettings ?? [];
        const applicationName = String(
            settings.find((x) => x.name === "ApplicationName")?.value ?? "",
        );
        const clubId = Number(
            settings.find((x) => x.name === "ClubId")?.value ?? 0,
        );

        const req: DeepLinkRequest = {
            applicationName,
            clubId,
            email,
            verificationCode: emailVerificationCode,
        };

        const res = await authStore.deepLinkSignin(req);
        if (res?.isSuccess) {
            const { email: _email, emailverificationcode: _code, ...restQuery } = to.query;
            return { path: to.path, query: restQuery };
        }
    }

    const requiresAuth = to.matched.some((r) => r.meta?.requireAuth === true);

    if (!authStore.isAuthenticated && requiresAuth) {
        await authStore.checkAuthentication();
    }

    if (to.name === "signin" || to.name === "join" || to.name === "root") {
        await authStore.checkAuthentication();
    }

    if (
        authStore.isAuthenticated &&
        (to.name === "signin" ||
            to.name === "join" ||
            to.name === "root" ||
            to.name === "forgot-password")
    ) {
        return { name: "home" };
    }

    if (requiresAuth && !authStore.isAuthenticated) {
        return { name: "signin", query: { returnUrl: to.fullPath } };
    }

    return true;
});

router.afterEach(async (to) => {
    const tenantConfig = useTenantConfig();
    const siteName =
        tenantConfig.settings.value?.tenantSettings?.find(
            (x) => x.name === "ApplicationDisplayName",
        )?.value ?? "";

    if (to.meta.title) {
        const title = (to.meta.title as string).replace(/{siteName}/g, siteName);

        const description = to.meta.description
            ? (to.meta.description as string).replace(/{siteName}/g, siteName)
            : `Welcome to ${siteName}. Explore our features and rewards.`;

        usePageMeta(title, description);
    }

    const log = ref<memberLogRequest>({
        path: `${window.location.origin}${to.fullPath}`,
    });

    loggingService.logPageView(log.value);

    // Mirror the legacy Razor Pages site's per-page dataLayer push (isLoggedIn/
    // memberId), so tags/triggers relying on these values re-evaluate them on
    // every SPA navigation, not just once at app boot.
    const authStore = useAuthStore();
    pushDataLayer({
        isLoggedIn: authStore.isAuthenticated,
        memberId: authStore.member.memberId ?? "",
    });

    // Fire a dedicated event whenever memberId is known on an SPA route
    // change (as opposed to "member_identified", which fires on normal/full
    // page loads via the auth state watcher in main.ts), so consent/marketing
    // triggers gated on memberId have a reliable signal on navigation too.
    if (authStore.isAuthenticated && authStore.member.memberId) {
        pushDataLayer({ event: "member_identified_route" });
    }

    // Push a virtual pageview to GTM's dataLayer on every SPA route change,
    // since GTM's own gtm.js snippet only fires once on initial page load.
    pushDataLayer({
        event: "page_view",
        page_path: to.fullPath,
        page_title: to.meta.title,
    });
});

router.onError(() => {
    if (!router.currentRoute.value.path.startsWith("/errors"))
        router.replace("/errors/Error500");
});

export default router;
